import create, { UseStore } from "zustand";
import createVanilla, { StoreApi } from "zustand/vanilla";
import { persist } from "zustand/middleware";
import { StateStorage } from "client/zustand/notPublic";
import { IndexedDbStateStorage, IsolatedLocalStorageStateStorage } from "client/zustand/persistence";
import { shallowComparison } from "client/Misc/compareUtils";
import {
	decrypt,
	encrypt,
	SjclCipherEncrypted
} from "sjcl";
import { waitForUserConfigurationToInitialize } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { getEncryptionKey } from "client/Api/FrontEndEncryption/frontEndEncryption";
import { deferred, type Deferred } from "client/Misc/utils";
import {
	defineInitialVersion,
	MigrationMetadata,
	withMigrator,
	WithMigratorStorageValue
} from "client/zustand/migrations";

export type EnabledAppToConfigure = {
	installedAppId: number | null;
	name: string;
};

type PersistedEnabledApps = Record<number, readonly EnabledAppToConfigure[]>;

type SpaceTemplateEnabledAppsStoreData = {
	enabledApps: PersistedEnabledApps;
	setEnabledApps: (spaceId: number, apps: readonly EnabledAppToConfigure[]) => Promise<void>;
	clearEnabledApps: (spaceId: number) => Promise<void>;
};

export type SpaceTemplateEnabledAppsStorage = {
	apps: readonly EnabledAppToConfigure[] | undefined;
	setEnabledApps: (spaceId: number, apps: readonly EnabledAppToConfigure[]) => Promise<void>;
	clearEnabledApps: (spaceId: number) => Promise<void>;
};

export function useSpaceTemplateEnabledApps(spaceId?: number): SpaceTemplateEnabledAppsStorage {
	const {
		apps,
		setEnabledApps,
		clearEnabledApps
	} = useSpaceTemplateEnabledAppsStore(
		(x) => ({
			apps: spaceId === undefined
				? undefined
				: x.enabledApps[spaceId],
			setEnabledApps: x.setEnabledApps,
			clearEnabledApps: x.clearEnabledApps
		}),
		shallowComparison
	);

	return {
		apps,
		setEnabledApps,
		clearEnabledApps
	};
}

type PersistedModel = {
	content: SjclCipherEncrypted;
	encryptionPublic: string;
	version: number;
	migrationMetadata: Record<number, MigrationMetadata>;
};

async function serializeState(value: WithMigratorStorageValue<SpaceTemplateEnabledAppsStoreData>): Promise<string> {
	const {
		state,
		version,
		migrationMetadata
	} = value;

	const {
		databaseConfiguration: {
			encryptionKey,
			encryptionPublic
		}
	} = await waitForUserConfigurationToInitialize();

	const enabledAppsContent: string = JSON.stringify(state.enabledApps);
	const encryptedContent: SjclCipherEncrypted = encrypt(encryptionKey, enabledAppsContent);

	const model: PersistedModel = {
		content: encryptedContent,
		encryptionPublic,
		version,
		migrationMetadata
	};

	return JSON.stringify(model);
}

async function deserializeState(serializedState: string): Promise<WithMigratorStorageValue<SpaceTemplateEnabledAppsStoreData>> {
	const {
		databaseConfiguration: {
			encryptionKey: currentEncryptionKey,
			encryptionPublic: currentEncryptionPublic
		}
	} = await waitForUserConfigurationToInitialize();

	const model: PersistedModel = JSON.parse(serializedState);

	const encryptionKey: string = model.encryptionPublic === currentEncryptionPublic
		? currentEncryptionKey
		: await getEncryptionKey(model.encryptionPublic);

	const decryptedContent: string = decrypt(encryptionKey, model.content);
	const enabledApps: PersistedEnabledApps = JSON.parse(decryptedContent);

	const state: Partial<SpaceTemplateEnabledAppsStoreData> = {
		enabledApps
	};

	return {
		// NOTE type definitions for zustand are incorrect, StorageValue<SpaceTemplateEnabledAppsStoreData>
		// is expected to be returned here, and not just plain SpaceTemplateEnabledAppsStoreData.//
		state: state as SpaceTemplateEnabledAppsStoreData,
		version: model.version,
		migrationMetadata: model.migrationMetadata
	};
}

class SafeStateStorage implements StateStorage {
	private readonly _storage: Promise<StateStorage>;

	constructor() {
		const fallbackStorage = new IsolatedLocalStorageStateStorage();

		const primaryStorage = new IndexedDbStateStorage({
			onAfterSetItem: (name) => fallbackStorage.clear(name),
			shouldOnlyPersistDataForCurrentUser: false
		});

		this._storage = primaryStorage.checkIfAvailable()
			.then(isAvailable => isAvailable ? primaryStorage : fallbackStorage);
	}

	public async getItem(name: string): Promise<string | null> {
		const storage: StateStorage = await this._storage;
		return await storage.getItem(name);
	}

	public async setItem(name: string, value: string): Promise<void> {
		const storage: StateStorage = await this._storage;
		await storage.setItem(name, value);
	}
}

let spaceTemplateEnabledAppsStoreRehydration: Deferred<void> | null = deferred<void>();

const store: StoreApi<SpaceTemplateEnabledAppsStoreData> = createVanilla<SpaceTemplateEnabledAppsStoreData>(
	persist(
		(set): SpaceTemplateEnabledAppsStoreData => {
			const waitForRehydrationToFinish = async (): Promise<void> => {
				if (!spaceTemplateEnabledAppsStoreRehydration) {
					return;
				}

				try {
					await spaceTemplateEnabledAppsStoreRehydration.promise;
				} catch (e) {
					console.error("Rehydration from browser storage failed.", e);
				}

				spaceTemplateEnabledAppsStoreRehydration = null;
			};

			return {
				enabledApps: {},
				setEnabledApps: async (
					spaceId: number,
					apps: readonly EnabledAppToConfigure[]
				): Promise<void> => {
					await waitForRehydrationToFinish();

					set((previousState) => ({
						enabledApps: {
							...previousState.enabledApps,
							[spaceId]: apps
						}
					}));
				},
				clearEnabledApps: async (spaceId: number): Promise<void> => {
					await waitForRehydrationToFinish();

					set((previousState) => {
						const enabledApps: PersistedEnabledApps = { ...previousState.enabledApps };
						delete enabledApps[spaceId];

						return {
							enabledApps
						};
					});
				}
			};
		},
		withMigrator(
			{
				name: "space-template-enabled-apps",
				getStorage: () => new SafeStateStorage(),
				serialize: serializeState,
				deserialize: async (str) => await deserializeState(str),
				onRehydrateStorage: () => {
					return (_, error) => {
						if (spaceTemplateEnabledAppsStoreRehydration) {
							if (error) {
								spaceTemplateEnabledAppsStoreRehydration.reject(error);
							} else {
								spaceTemplateEnabledAppsStoreRehydration.resolve();
							}
						}
					};
				}
			},
			defineInitialVersion(1),
		)
	)
);

const useSpaceTemplateEnabledAppsStore: UseStore<SpaceTemplateEnabledAppsStoreData> = create(store);
